'use client';
import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import PageHero from '@/components/ui/PageHero';

const benefits = [
  { icon: 'CpuChipIcon', title: 'Latest Tech Stack', desc: 'Work with cutting-edge mobility tech, AI, and cloud infrastructure.' },
  { icon: 'HomeIcon', title: 'Remote-Friendly', desc: 'Flexible work arrangements. Most roles are hybrid or fully remote.' },
  { icon: 'AcademicCapIcon', title: 'Learning Budget', desc: 'Dedicated annual budget for courses, conferences, and certifications to fuel your growth.' },
  { icon: 'HeartIcon', title: 'Health Coverage', desc: 'Comprehensive health insurance for you and your family.' },
  { icon: 'ChartBarIcon', title: 'ESOPs', desc: 'Meaningful equity. You build it, you own a piece of it.' },
  { icon: 'UserGroupIcon', title: 'Small Team, Big Impact', desc: 'Work directly with founders. Your contributions are visible.' },
];

type JobCategory = 'All' | 'Engineering' | 'Mobile' | 'Design';

interface Job {
  id: number;
  title: string;
  category: JobCategory;
  location: string;
  experience: string;
}

const jobs: Job[] = [];

const tabs: JobCategory[] = ['All', 'Engineering', 'Mobile', 'Design'];

interface JobCardProps {
  job: Job;
}

function JobCard({ job }: JobCardProps) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-[rgba(15,23,42,0.07)] shadow-card hover:shadow-card-hover transition-all duration-300 group">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-3 group-hover:text-[#0066FF] transition-colors duration-200">
            {job.title}
          </h3>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-sm text-[#475569]">
              <Icon name="MapPinIcon" size={14} className="text-[#0066FF] flex-shrink-0" />
              {job.location}
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm text-[#475569]">
              <Icon name="BriefcaseIcon" size={14} className="text-[#0066FF] flex-shrink-0" />
              {job.experience}
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm text-[#475569]">
              <Icon name="TagIcon" size={14} className="text-[#0066FF] flex-shrink-0" />
              {job.category}
            </span>
          </div>
        </div>
        <div
          className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}
        >
          <Icon name="CodeBracketIcon" size={18} className="text-[#0066FF]" />
        </div>
      </div>
    </div>
  );
}

export default function CareersContent() {
  const [formData, setFormData] = useState({ name: '', email: '', role: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState<JobCategory>('All');
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleTabChange = (tab: JobCategory) => {
    if (tab === activeTab) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveTab(tab);
      setIsTransitioning(false);
    }, 150);
  };

  const filteredJobs = activeTab === 'All' ? jobs : jobs.filter((j) => j.category === activeTab);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <PageHero className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-on-scroll stagger-1">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/15 text-[11px] font-semibold tracking-widest text-white uppercase mb-6">
              Join Our Team
            </span>
            <h1 className="font-display text-5xl md:text-7xl font-semibold text-white tracking-tight leading-tight mb-6 mt-2">
              Build the future of{' '}
              <span className="italic font-light text-white/80">mobility.</span>
            </h1>
            <p className="text-white/70 text-xl max-w-2xl mx-auto leading-relaxed animate-on-scroll stagger-2">
              We're a small team doing big things. If you love solving hard problems in mobility and logistics, we want to hear from you.
            </p>
          </div>
          <div className="animate-on-scroll stagger-3 mt-12 flex justify-center gap-8 flex-wrap">
            {[
              { value: '0', label: 'Open Roles' },
              { value: '5+', label: 'Team Members' },
              { value: '100%', label: 'Ownership Culture' },
            ].map((s) => (
              <div key={s.label} className="text-center px-6 py-3 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-sm">
                <div className="font-display text-3xl font-semibold text-white">{s.value}</div>
                <div className="text-sm text-white/85 font-medium mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </PageHero>

      {/* Benefits */}
      <div className="py-24 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 mb-16">
            <span className="section-label block mb-3">Why Movio</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              More than a job.{' '}
              <span className="italic font-light text-[#0066FF]">A mission.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                className={`animate-on-scroll stagger-${Math.min(i + 2, 6)} bg-white rounded-2xl p-8 border border-[rgba(15,23,42,0.07)] shadow-card group hover:shadow-card-hover transition-all duration-300`}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}>
                  <Icon name={b.icon as Parameters<typeof Icon>[0]['name']} size={22} className="text-[#0066FF]" />
                </div>
                <h3 className="font-display text-lg font-semibold text-[#0F172A] mb-2">{b.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Find Your Role */}
      <div className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 mb-10">
            <span className="section-label block mb-3">Open Positions</span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight">
              Find your{' '}
              <span className="italic font-light text-[#0066FF]">role.</span>
            </h2>
          </div>

          {/* Job Listings */}
          <div
            role="tabpanel"
            className={`transition-opacity duration-150 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
          >
            {filteredJobs.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredJobs.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            ) : (
              <div className="bg-[#F5F5F5] rounded-2xl border border-[rgba(15,23,42,0.07)] p-16 flex flex-col items-center text-center">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}
                >
                  <Icon name="BriefcaseIcon" size={28} className="text-[#0066FF]" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-[#0F172A] mb-3">No openings available</h3>
                <p className="text-[#475569] text-base max-w-md leading-relaxed">
                  There are no open positions in this category right now. Check back soon or submit an open application below.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Apply Form */}
      <div id="apply" className="py-24 bg-[#F5F5F5]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-on-scroll stagger-1 text-center mb-12">
            <h2 className="font-display text-4xl font-semibold text-[#0F172A] mb-4">
              Don't see your role?{' '}
              <span className="italic font-light text-[#0066FF]">Apply anyway.</span>
            </h2>
            <p className="text-[#475569] text-lg">We're always looking for exceptional people.</p>
          </div>

          {submitted ? (
            <div className="animate-on-scroll is-visible bg-white rounded-2xl p-12 border border-[rgba(15,23,42,0.07)] shadow-card text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #0066FF20, #075DDF10)' }}>
                <Icon name="CheckCircleIcon" size={32} className="text-[#0066FF]" />
              </div>
              <h3 className="font-display text-2xl font-semibold text-[#0F172A] mb-2">Application Sent!</h3>
              <p className="text-[#475569]">We'll review your profile and reach out within 5 business days.</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="animate-on-scroll stagger-2 bg-white rounded-2xl p-8 md:p-10 border border-[rgba(15,23,42,0.07)] shadow-card flex flex-col gap-5"
            >
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">Full Name</label>
                  <input
                    className="form-input"
                    placeholder="Arjun Sharma"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="arjun@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Role of Interest</label>
                <select
                  className="form-input"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  <option value="">Select a role...</option>
                  <option value="Fullstack Developer (Laravel)">Fullstack Developer (Laravel)</option>
                  <option value="Frontend Developer (React, Next.js)">Frontend Developer (React, Next.js)</option>
                  <option value="Backend Developer (Python, Django)">Backend Developer (Python, Django)</option>
                  <option value="DevOps Engineer (AWS, GCP, FCM)">DevOps Engineer (AWS, GCP, FCM)</option>
                  <option value="Testing Engineer (Manual, Automation)">Testing Engineer (Manual, Automation)</option>
                  <option value="UI/UX Designer">UI/UX Designer</option>
                  <option value="Other">Other / Open Application</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Why Movio?</label>
                <textarea
                  className="form-input"
                  rows={4}
                  placeholder="Tell us about yourself and what excites you about mobility tech..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  style={{ resize: 'vertical' }}
                />
              </div>
              <button type="submit" className="btn-primary px-8 py-4 rounded-xl text-base font-semibold flex items-center justify-center gap-2.5 w-full">
                Submit Application
                <Icon name="PaperAirplaneIcon" size={18} />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}